// @ts-ignore
import React, { JSX } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

const sectionCss = css({
    [media.above.sm]: {
        paddingBlockEnd: "64px"
    },
    margin: "0 auto",
    maxWidth: "1200px",
    paddingBlockStart: "64px",
    a: {
        "color": "#0052cc",
        "textDecoration": "underline",
        "&:hover": {
            color: "#0065ff",
            textDecoration: "none"
        }
    },
    [media.below.sm]: {
        paddingBlockEnd: 0
    }
});

const headerCss = css({
    [media.above.sm]: {
        fontSize: "2.25rem",
        width: "66.66666%"
    },
    color: "#253858",
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontWeight: 500,
    lineHeight: 1.33333333,
    margin: "0 auto",
    padding: "32px 16px 48px",
    textAlign: "center",
    [media.below.sm]: {
        fontSize: "1.5rem",
        paddingBlockStart: "32px",
        width: "100%"
    }
});

const cardsWrapperCss = css({
    "alignItems": "stretch",
    "display": "flex",
    "gap": "32px",
    "padding": "0 16px",
    [media.below.sm]: {
        flexDirection: "column",
        margin: "0 auto",
        width: "66.66666%"
    },
    "@media (max-width: 540px)": {
        width: "100%"
    }
});

const cardWrapperCss = css({
    alignItems: "center",
    backgroundColor: "#ffffff",
    borderColor: "#f4f5f7",
    boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "4px",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    lineHeight: 1.71428571,
    textAlign: "center",
    padding: "0 0 54px 0"
});

const costCss = css({
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontSize: "44px",
    fontWeight: 600,
    lineHeight: 1,
    marginBlockEnd: "12px",
    marginBlockStart: "40px"
});

const costLabelCss = css({
    color: "#172b4d",
    fontSize: "1.25rem",
    lineHeight: 1.2
});

const quantityCss = css({
    color: "#172b4d",
    fontSize: "0.875rem",
    lineHeight: 1.71428571
});

const footerWrapperCss = css({
    margin: "32px 16px",
    padding: 0,
    p: {
        margin: 0,
        padding: "40px"
    }
});

type PricingTiersProps = {
    header: string;
    cards: PricingCardProps[];
    footer: JSX.Element;
};

export type PricingCardProps = {
    ctaEmphasis: boolean;
    tier: string;
    cost: string;
    costLabel: string;
    quantity: string;
    cta: string;
    ctaUrl: string;
    description1: string;
    description2: string;
    linkText?: string;
    linkUrl?: string;
};

const PricingCard = ({
    ctaEmphasis,
    tier,
    cost,
    costLabel,
    quantity,
    cta,
    ctaUrl,
    description1,
    description2,
    linkText,
    linkUrl
}: PricingCardProps) => {
    const cardHeaderCss = [
        ctaEmphasis &&
            css({
                background: "#f4f5f7",
                fontSize: "1.5rem",
                fontWeight: 500,
                letterSpacing: "0.3px",
                lineHeight: 1.33333333,
                margin: 0,
                padding: "24px 12px",
                width: "100%"
            }),
        !ctaEmphasis &&
            css({
                background: "none",
                fontSize: "1.5rem",
                fontWeight: 500,
                letterSpacing: "0.3px",
                lineHeight: 1.33333333,
                margin: 0,
                padding: "24px 12px",
                width: "100%"
            })
    ];

    const ctaCss = [
        ctaEmphasis &&
            css({
                "background": "#0052cc",
                "color": "#ffffff !important",
                "fontWeight": 500,
                "&:hover": {
                    background: "#0047b3",
                    borderColor: "#0047b3"
                },
                "border": "1px solid #0052cc",
                "borderRadius": "3px",
                "display": "block",
                "fontSize": "16px",
                "lineHeight": 1.5,
                "marginBlockEnd": "24px",
                "marginBlockStart": "32px",
                "padding": "0.4375rem 16px",
                "textDecoration": "none !important",
                "transition": "all 250ms"
            }),
        !ctaEmphasis &&
            css({
                "background": "transparent",
                "color": `${"#0052cc"} !important`,
                "fontWeight": 400,
                "&:hover": {
                    background: "#ebecf0",
                    borderColor: "#0047b3"
                },
                "border": "1px solid #0052cc",
                "borderRadius": "3px",
                "display": "block",
                "fontSize": "16px",
                "lineHeight": 1.5,
                "marginBlockEnd": "24px",
                "marginBlockStart": "32px",
                "padding": "0.4375rem 16px",
                "textDecoration": "none !important",
                "transition": "all 250ms"
            })
    ];

    return (
        <div css={cardWrapperCss}>
            <h3 css={cardHeaderCss}>{tier}</h3>
            <div css={costCss}>{cost}</div>
            <div css={costLabelCss}>{costLabel}</div>
            <div css={quantityCss}>{quantity}</div>
            <a href={ctaUrl} css={ctaCss}>
                {cta}
            </a>
            <div>{description1}</div>
            <div>{description2}</div>
            {linkText && linkUrl && <a href={linkUrl}>{linkText}</a>}
        </div>
    );
};

export const PricingTiers = ({ header, cards, footer }: PricingTiersProps) => {
    return (
        <section css={sectionCss}>
            <h2 css={headerCss}>{header}</h2>
            <div css={cardsWrapperCss}>
                {cards.map((card, index) => (
                    <PricingCard key={`PricingCard-${index}`} {...card} />
                ))}
            </div>
            {footer && (
                <div css={[cardWrapperCss, footerWrapperCss]}>{footer}</div>
            )}
        </section>
    );
};

export default PricingTiers;
